import { apiClient } from './apiClient';
import type { AgencyInfo, Space, SpaceRoleType } from '../types/auth';
import { Logger } from '../utils/logger';

export interface AgencySpaceInfo {
  spaceId: string;
  spaceName?: string;
  spaceSlug?: string;
  logoUrl?: string;
  role: SpaceRoleType;
  clientEmails: string[]; // invités lecture seule (V4-AGENCY-MULTI-GUEST)
}

export interface AgencyMemberInfo {
  id: string;
  userId: string;
  role: string;
  userEmail?: string;
  userFullName?: string;
  assignedSpaces: string[];
}

export interface AgencyBillingSummary {
  agencyId: string;
  agencyName?: string;
  planName?: string;
  totalSeats: number;
  includedSeats: number;
  additionalSeats: number;
  basePrice: number;
  pricePerAdditionalSeat: number;
  additionalCost: number;
  totalMonthlyCost: number;
}

// V4-FRONT-ANY-USAGE 2026-05-28 — backend payload shapes (snake_case) for /agency/me routes.
interface BackendAgencySpace {
  space_id: string;
  space_name?: string;
  space_slug?: string;
  logo_url?: string;
  role: SpaceRoleType;
  client_emails?: string[];
}

// V4-FRONT-ANY-USAGE batch 6 2026-05-29 — DTOs snake_case du backend agency
interface BackendAgency {
  id: string;
  name: string;
  slug: string;
  owner_user_id: string;
  billing_email?: string;
  role: AgencyInfo['role'];
  member_count: number;
}

interface BackendAgencyBilling {
  agency_id: string;
  agency_name: string;
  plan_name: string;
  total_seats: number;
  included_seats: number;
  additional_seats: number;
  base_price: number;
  price_per_additional_seat: number;
  additional_cost: number;
  total_monthly_cost: number;
}

interface BackendCheckoutSession {
  checkout_url: string;
}

interface BackendAgencyMember {
  id: string;
  user_id: string;
  role: string;
  user_email?: string;
  user_full_name?: string;
  assigned_spaces?: string[];
}

export const agencyService = {
  /**
   * Retourne l'agence du user courant, ou null s'il n'appartient à aucune agence.
   */
  async getMyAgency(): Promise<AgencyInfo | null> {
    Logger.debug('[AgencyService] Fetching current agency');
    const response = await apiClient.get<BackendAgency | null>('/agency/me');
    const data = response.data;
    if (!data) {
      Logger.info('[AgencyService] No current agency found');
      return null;
    }
    Logger.info('[AgencyService] Current agency fetched', { agencyId: data.id, role: data.role });
    return {
      id: data.id,
      name: data.name,
      slug: data.slug,
      ownerUserId: data.owner_user_id,
      billingEmail: data.billing_email,
      role: data.role,
      memberCount: data.member_count,
    };
  },

  /**
   * Retourne les espaces accessibles dans l'agence du user courant.
   */
  async getMyAgencySpaces(): Promise<AgencySpaceInfo[]> {
    Logger.debug('[AgencyService] Fetching agency spaces');
    // V4-FRONT-ANY-USAGE 2026-05-28
    const response = await apiClient.get<BackendAgencySpace[]>('/agency/me/spaces');
    const spaces = response.data.map((s) => ({
      spaceId: s.space_id,
      spaceName: s.space_name,
      spaceSlug: s.space_slug,
      logoUrl: s.logo_url,
      role: s.role,
      clientEmails: s.client_emails ?? [],
    }));
    Logger.info('[AgencyService] Agency spaces fetched', { count: spaces.length });
    return spaces;
  },

  /**
   * Retourne les membres de l'agence (owner seulement).
   */
  async getMyAgencyMembers(): Promise<AgencyMemberInfo[]> {
    Logger.debug('[AgencyService] Fetching agency members');
    // V4-FRONT-ANY-USAGE 2026-05-28
    const response = await apiClient.get<BackendAgencyMember[]>('/agency/me/members');
    const members = response.data.map((m) => ({
      id: m.id,
      userId: m.user_id,
      role: m.role,
      userEmail: m.user_email,
      userFullName: m.user_full_name,
      assignedSpaces: m.assigned_spaces || [],
    }));
    Logger.info('[AgencyService] Agency members fetched', { count: members.length });
    return members;
  },

  /**
   * Récupère le résumé de facturation (owner seulement).
   */
  async getMyAgencyBilling(): Promise<AgencyBillingSummary> {
    Logger.debug('[AgencyService] Fetching agency billing summary');
    const response = await apiClient.get<BackendAgencyBilling>('/agency/me/billing');
    const d = response.data;
    Logger.info('[AgencyService] Agency billing summary fetched', {
      agencyId: d.agency_id,
      totalSeats: d.total_seats,
      additionalSeats: d.additional_seats,
    });
    return {
      agencyId: d.agency_id,
      agencyName: d.agency_name,
      planName: d.plan_name,
      totalSeats: d.total_seats,
      includedSeats: d.included_seats,
      additionalSeats: d.additional_seats,
      basePrice: d.base_price,
      pricePerAdditionalSeat: d.price_per_additional_seat,
      additionalCost: d.additional_cost,
      totalMonthlyCost: d.total_monthly_cost,
    };
  },

  /**
   * Crée un nouvel espace agence.
   */
  async createAgencySpace(name: string, clientEmails?: string[]): Promise<AgencySpaceInfo> {
    Logger.info('[AgencyService] Creating agency space', {
      name,
      guestCount: clientEmails?.length ?? 0,
    });
    const response = await apiClient.post<BackendAgencySpace>('/agency/me/spaces', {
      name,
      client_emails: clientEmails && clientEmails.length ? clientEmails : undefined,
    });
    const s = response.data;
    Logger.info('[AgencyService] Agency space created', { spaceId: s.space_id, name: s.space_name });
    return {
      spaceId: s.space_id,
      spaceName: s.space_name,
      spaceSlug: s.space_slug,
      logoUrl: s.logo_url,
      role: s.role,
      clientEmails: s.client_emails ?? [],
    };
  },

  /**
   * Met à jour un espace agence (nom + réconciliation des invités lecture seule).
   * `clientEmails` non fourni → ne touche pas aux invités ; fourni (même []) → réconcilie.
   */
  async updateAgencySpace(spaceId: string, data: { name?: string; clientEmails?: string[] }): Promise<AgencySpaceInfo> {
    Logger.info('[AgencyService] Updating agency space', {
      spaceId,
      hasName: !!data.name,
      guestCount: data.clientEmails?.length,
    });
    const response = await apiClient.patch<BackendAgencySpace>(`/agency/me/spaces/${spaceId}`, {
      name: data.name || undefined,
      client_emails: data.clientEmails,
    });
    const s = response.data;
    Logger.info('[AgencyService] Agency space updated', { spaceId: s.space_id, name: s.space_name });
    return {
      spaceId: s.space_id,
      spaceName: s.space_name,
      spaceSlug: s.space_slug,
      logoUrl: s.logo_url,
      role: s.role,
      clientEmails: s.client_emails ?? [],
    };
  },

  /**
   * Archive un espace agence (soft delete).
   */
  async deleteAgencySpace(spaceId: string): Promise<void> {
    Logger.warn('[AgencyService] Archiving agency space', { spaceId });
    await apiClient.delete(`/agency/me/spaces/${spaceId}`);
    Logger.info('[AgencyService] Agency space archived', { spaceId });
  },

  /**
   * Downgrade AGENCE→PRO : ne garde que `keepSpaceId`, archive les autres
   * (inaccessibles), dissout l'agence. Réservé à l'owner. Le front DOIT
   * confirmer explicitement avant l'appel (action destructive). V4-ACCOUNT-SWITCH.
   */
  async downgradeToPro(keepSpaceId: string): Promise<AgencySpaceInfo> {
    Logger.warn('[AgencyService] Downgrading agency to pro', { keepSpaceId });
    const response = await apiClient.post<BackendAgencySpace>('/agency/me/downgrade-to-pro', {
      keep_space_id: keepSpaceId,
    });
    const s = response.data;
    Logger.info('[AgencyService] Agency downgraded to pro', { keptSpaceId: s.space_id });
    return {
      spaceId: s.space_id,
      spaceName: s.space_name,
      spaceSlug: s.space_slug,
      logoUrl: s.logo_url,
      role: s.role,
      clientEmails: s.client_emails ?? [],
    };
  },

  /**
   * Achète un ou plusieurs sièges supplémentaires via Stripe.
   */
  async purchaseSeat(quantity: number = 1): Promise<{ checkoutUrl: string }> {
    Logger.info('[AgencyService] Purchasing additional agency seats', { quantity });
    const response = await apiClient.post<BackendCheckoutSession>('/agency/me/purchase-seat', { quantity });
    Logger.info('[AgencyService] Additional agency seats purchase session created', {
      quantity,
      hasCheckoutUrl: !!response.data.checkout_url,
    });
    return { checkoutUrl: response.data.checkout_url };
  },
};
